/** 微信授权登录 / 静默绑定 openId - 作者: zbq, 日期: 2026-08-16 */
import { isWxBrowser } from '@/sheep/helper/utils';
import { ensureSiteDomain, getCachedPromoOrigin } from '@/sheep/helper/zq-site-domain';
import { zqCall, getUserId } from './helpers';
import { persistSharePromoForOAuth, restoreSharePromoFromCookie, getShareInvite } from './share';

/** 微信登录（需用户确认，可拉头像昵称） */
export const OAUTH_STATE_LOGIN = 'zq_wx_login';
/** 账号密码登录后静默补 openId（snsapi_base，一般无感） */
export const OAUTH_STATE_SILENT_BIND = 'zq_wx_silent_bind';
/** 后台商家扫码绑定微信收款 openId */
export const OAUTH_STATE_PAY_BIND = 'zq_wx_pay_bind';

const PENDING_SILENT_KEY = 'zq_pending_silent_openid';
const PAY_BIND_TICKET_KEY = 'zq_wx_pay_bind_ticket';

/**
 * OAuth 回调根：后台「推广域」promoDomain；未配则回退当前页。
 * 须在微信公众号「网页授权域名」报备该域，否则 10003。
 */
function getOAuthRedirectUrl() {
  // #ifdef H5
  const promo = getCachedPromoOrigin();
  if (promo) {
    return promo.endsWith('/') ? promo : `${promo}/`;
  }
  const { origin } = window.location;
  return origin.endsWith('/') ? origin : `${origin}/`;
  // #endif
  return '';
}

/** 从 URL 解析 OAuth 回调参数 */
export function getUrlCode(name) {
  // #ifdef H5
  const match = new RegExp('[?|&]' + name + '=([^&;]+?)(&|#|;|$)').exec(location.href);
  if (!match || !match[1]) return null;
  return decodeURIComponent(match[1].replace(/\+/g, '%20'));
  // #endif
  return null;
}

/** 清除 URL 中的 code/state，避免重复登录；尽量保留 invite */
export function removeCodeFromUrl() {
  // #ifdef H5
  const url = new URL(window.location.href);
  if (!url.searchParams.has('code') && !url.searchParams.has('state')) return;
  restoreSharePromoFromCookie();
  const invite = url.searchParams.get('invite') || uni.getStorageSync('wxinvite') || '';
  url.search = invite ? `invite=${encodeURIComponent(invite)}` : '';
  window.history.replaceState(null, '', url.toString());
  // #endif
}

/**
 * @param {'login'|'silent'|'pay_bind'} mode
 */
function buildOAuthCallbackUrl(pageUrl) {
  persistSharePromoForOAuth();
  restoreSharePromoFromCookie();
  const invite = getShareInvite();
  if (!invite) {
    return pageUrl;
  }
  try {
    const u = new URL(pageUrl);
    u.searchParams.set('invite', invite);
    return u.toString();
  } catch (e) {
    return pageUrl;
  }
}

async function redirectToWxOAuth(mode = 'login') {
  // #ifdef H5
  persistSharePromoForOAuth();
  await ensureSiteDomain();
  const pageUrl = getOAuthRedirectUrl();
  const res = await zqCall('api/getWxConfig', { url: pageUrl }, { showLoading: false });
  const appId = res.data?.h5Appid;
  if (res.code !== 0 || !appId) {
    if (mode === 'login' || mode === 'pay_bind') {
      uni.showToast({ title: res.msg || '获取微信配置失败', icon: 'none' });
    }
    return false;
  }
  const scope = mode === 'login' ? 'snsapi_userinfo' : 'snsapi_base';
  const state =
    mode === 'silent'
      ? OAUTH_STATE_SILENT_BIND
      : mode === 'pay_bind'
        ? OAUTH_STATE_PAY_BIND
        : OAUTH_STATE_LOGIN;
  const redirectUri = encodeURIComponent(buildOAuthCallbackUrl(pageUrl));
  const oauthUrl =
    'https://open.weixin.qq.com/connect/oauth2/authorize' +
    `?appid=${appId}` +
    `&redirect_uri=${redirectUri}` +
    '&response_type=code' +
    `&scope=${scope}` +
    `&state=${state}#wechat_redirect`;
  window.location.href = oauthUrl;
  return true;
  // #endif
  return false;
}

/** App/小程序：uni.login 获取 code */
function loginByUniProvider() {
  return new Promise((resolve) => {
    uni.login({
      provider: 'weixin',
      onlyAuthorize: true,
      success(res) {
        if (res.errMsg === 'login:ok' && res.code) {
          resolve(res.code);
          return;
        }
        uni.showToast({ title: res.errMsg || '微信授权失败', icon: 'none' });
        resolve(null);
      },
      fail() {
        uni.showToast({ title: '微信登录失败，请重试', icon: 'none' });
        resolve(null);
      },
    });
  });
}

/**
 * 获取微信 OAuth code（显式登录）
 * - H5 微信内：无 code 时跳转授权页，有 code 时直接返回
 */
export async function obtainWxCode() {
  // #ifdef H5
  restoreSharePromoFromCookie();
  const code = getUrlCode('code');
  if (code) return code;
  if (!isWxBrowser()) {
    uni.showToast({ title: '请在微信中打开后授权登录', icon: 'none' });
    return null;
  }
  await redirectToWxOAuth('login');
  return null;
  // #endif

  // #ifdef APP-PLUS || MP-WEIXIN
  return loginByUniProvider();
  // #endif

  uni.showToast({ title: '当前环境不支持微信登录', icon: 'none' });
  return null;
}

function hasLocalOpenId() {
  return !!(
    uni.getStorageSync('openid') ||
    uni.getStorageSync('openId') ||
    uni.getStorageSync('wx_openid')
  );
}

function cacheOpenId(openid) {
  if (!openid) return;
  try {
    uni.setStorageSync('openid', openid);
    uni.setStorageSync('openId', openid);
  } catch (e) {
    /* ignore */
  }
}

/**
 * 向服务端要会员的 openid（会员表 zq_member.openid）。
 *
 * 为什么必须先问服务端：老客户早年在 zqss88 域授权过，openid 记在会员表里，
 * 但换手机 / 清缓存 / 换浏览器后本地 storage 是空的。旧逻辑只看本地，
 * 于是又去跳公众号授权；而现在授权回调域是推广域，公众号没报备就会 10003
 * （「微信登录失败 redirect_uri域名与后台配置不一致」），付款直接卡死。
 *
 * @returns {Promise<string>} 拿不到返回空串
 */
export async function fetchServerOpenId() {
  // #ifdef H5
  const uid = getUserId();
  if (!uid) return '';
  try {
    const res = await zqCall('api/getMemberById', { id: uid }, { showLoading: false });
    const data = (res && res.code === 0 && res.data) || {};
    return data.openid || data.openId || '';
  } catch (e) {
    return '';
  }
  // #endif
  return '';
}


/**
 * 确保本地已有 openId：微信内且本地无 openId 时，静默 snsapi_base 授权补齐。
 *
 * 重要：该授权是「整页跳转微信 → 回跳重载」，单次耗时 2~3 秒，
 * 因此**绝不能放在登录成功的关键跳转路径上**（会把登录跳转卡住）。
 * 只应在真正需要 openId 的操作前按需调用：
 *   - 微信支付：pages/shop/gooddetail.vue、pages/mine/paypage.vue
 *   - 微信提现：pages/mine/withdraw.vue
 * 补齐成功后由登录页 doSilentBind 写入会员表并缓存本地 storage（setZqSession），
 * 之后 hasLocalOpenId() 为真，不会重复跳转。
 *
 * @returns {'ok'|'skip'|'redirecting'} redirecting=已发起整页跳转，调用方应直接 return
 */
export async function ensureOpenIdForPay() {
  // #ifdef H5
  if (!isWxBrowser()) return 'skip';
  if (hasLocalOpenId()) return 'ok';
  if (!getUserId()) return 'skip';
  // 先问服务端：会员表已有 openid（老客户授权过）就直接复用，无需再跳公众号授权
  const remote = await fetchServerOpenId();
  if (remote) {
    cacheOpenId(remote);
    return 'ok';
  }
  uni.setStorageSync(PENDING_SILENT_KEY, '1');
  const ok = await redirectToWxOAuth('silent');
  return ok ? 'redirecting' : 'skip';
  // #endif
  return 'skip';
}

/** 兼容旧调用名（历史曾在账号密码登录后调用，会阻塞登录跳转）；行为同 ensureOpenIdForPay */
export async function ensureSilentOpenIdAfterLogin() {
  return ensureOpenIdForPay();
}

/** 是否为静默绑定回调 */
export function isSilentBindCallback() {
  return getUrlCode('state') === OAUTH_STATE_SILENT_BIND;
}

export function isPayBindCallback() {
  return getUrlCode('state') === OAUTH_STATE_PAY_BIND;
}

export function clearSilentBindPending() {
  uni.removeStorageSync(PENDING_SILENT_KEY);
}

export function isSilentBindPending() {
  return uni.getStorageSync(PENDING_SILENT_KEY) === '1';
}

export function savePayBindTicket(ticket) {
  if (ticket) uni.setStorageSync(PAY_BIND_TICKET_KEY, String(ticket));
}

export function loadPayBindTicket() {
  return uni.getStorageSync(PAY_BIND_TICKET_KEY) || '';
}

export function clearPayBindTicket() {
  uni.removeStorageSync(PAY_BIND_TICKET_KEY);
}

/**
 * 后台商家扫码绑定：微信内静默授权拿 code
 * @returns {'ok'|'skip'|'redirecting'}
 */
export async function startPayBindOAuth(ticket) {
  // #ifdef H5
  if (!ticket) return 'skip';
  if (!isWxBrowser()) {
    uni.showToast({ title: '请用微信扫一扫打开', icon: 'none' });
    return 'skip';
  }
  savePayBindTicket(ticket);
  const code = getUrlCode('code');
  if (code && isPayBindCallback()) return 'ok';
  const ok = await redirectToWxOAuth('pay_bind');
  return ok ? 'redirecting' : 'skip';
  // #endif
  return 'skip';
}

/** H5 使用 otherLogin，App/小程序使用 otherAppLogin */
export function getWxLoginApi() {
  // #ifdef H5
  return 'api/otherLogin';
  // #endif
  // #ifdef APP-PLUS || MP-WEIXIN
  return 'api/otherAppLogin';
  // #endif
  return 'api/otherLogin';
}
